/**
 * **늦게 도착한 응답을 버리는 관용구 하나** — 의존성이 없는 순수 모듈이다(Vue·`@/` 별칭 금지). 짝 둘이 같은 파일에 있다 —
 * 나가 있는 읽기를 한 곳에서 아는 [readGate], 한 대상에 가는 요청을 한 줄로 세우는 [requestLane].
 *
 * 같은 축에 요청이 겹치면(매장을 바꾸는 중 이전 매장의 응답이 도착, 빠르게 두 번 새로고침) **늦게 온 옛 응답이
 * 최신 화면을 덮는다.** 답은 언제나 같다: 요청마다 번호를 매기고, 커밋 직전에 그 번호가 아직 최신인지 묻는다.
 *
 * 그 관용구가 `PartnerLinkPanel` 과 네이버 위저드 두 곳에 복사되기 시작해 여기로 접었다. 번호는 클로저 안에
 * 살고 반환 객체는 얼려 둔다 — Vue 2 의 `observe()` 는 확장 불가 객체를 건너뛰므로 `data()` 에 그대로 담아도
 * 반응형 프록시가 끼어들지 않는다.
 *
 * ```js
 * data() { return { guard: latestGuard() } }
 * async load() {
 *   const token = this.guard.begin()
 *   const res = await api()
 *   if (!this.guard.isLatest(token)) return   // 그 사이 새 요청이 나갔다 — 이 응답은 버린다
 *   this.rows = res.data
 * }
 * ```
 *
 * **축마다 하나씩** 만든다. 동시에 나가는 다른 종류의 요청(업체 목록과 약관 상태)이 한 번호를 나눠 쓰면
 * 뒤에 시작한 쪽이 앞의 것을 무효로 만들어 **둘 다 성공해도 하나가 버려진다.**
 *
 * ## 떠나는 화면은 **닫는다**(`beforeDestroy` → [close]) — 무효화(`invalidate`)가 아니다
 *
 * `invalidate` 는 「지금까지의 응답」만 버린다. 가드가 없는 앞 단계의 legacy 콜백(매장 후보·세션 매장 조회)은 화면을
 * 떠난 뒤에도 돌고, 거기서 `begin()` 으로 **새 회차**를 시작하면 그 회차는 최신이라 떠난 화면이 다시 커밋한다 —
 * 옮겨 간 화면에 앞 화면의 안내가 뜬다(R-H 2차 리뷰). 닫힌 가드는 **그 뒤의 모든 토큰이 최신이 아니다.** 요청 자체는
 * 나갈 수 있지만(부르는 자리가 막지 않는다) 그 응답은 아무것도 쓰지 않는다. 닫힘은 되돌아가지 않는다 — 떠난
 * 인스턴스는 다시 쓰이지 않는다(대상만 바뀐 화면은 `invalidate` 를 쓴다).
 */
export function latestGuard() {
  let seq = 0
  let closed = false

  return Object.freeze({
    /** 새 요청 — 번호를 올려 돌려준다. 이 순간 이전 번호들은 전부 낡았다(닫힌 가드의 번호는 태어날 때부터 낡았다) */
    begin() {
      seq += 1
      return seq
    },
    /**
     * 이 번호가 아직 최신인가 — 커밋 직전에 묻는다.
     *
     * 번호는 1부터 나가므로 **0·빈 값은 언제나 거짓**이다: 아직 아무 요청도 시작하지 않은 가드가 「최신」이라고
     * 답하면, 토큰을 받아 두지 않은 호출부가 남의 응답을 커밋한다.
     */
    isLatest(token) {
      return !closed && token > 0 && token === seq
    },
    /** 보낸 요청 없이 번호만 올린다 — 「지금까지의 응답은 전부 버린다」(대상이 바뀌었을 때) */
    invalidate() {
      seq += 1
    },
    /** 화면을 떠난다 — 지금까지의 응답도, **이 뒤에 시작하는 회차**도 최신이 아니다(되돌아가지 않는다) */
    close() {
      closed = true
    },
  })
}

/**
 * **나가 있는 읽기를 한 곳에서 안다** — [latestGuard] 의 짝이고 푸는 문제가 다르다.
 *
 * `latestGuard` 는 「늦게 온 응답이 최신을 덮지 않게」(한 축의 순서)를 풀고, 이 문은 「같은 것을 두 번
 * 읽지 않게, 그리고 대상이 바뀌면 나가 있던 응답을 버리게」(여러 축의 겹침)를 푼다. 요청 자리마다 플래그를
 * 덧대는 대신 **읽기 한 곳**이 이 문을 지나게 해서, 세 가지가 한 규칙에서 나온다:
 *
 * - **중복 안 보냄** — 같은 키가 이미 나가 있으면 [start] 가 `null` 이다. 「더 보기」를 두 번 빠르게
 *   누르면 같은 장을 두 번 받아 같은 매장이 두 줄이 되고 다음 장에 영영 못 가던 것을 여기서 막는다.
 * - **버림** — [cancel] 로 표시된 티켓의 응답은 [isCurrent] 가 거짓이라 호출부가 커밋하지 않는다.
 *   검색어를 지우면 나가 있던 검색이 돌아와도 화면을 건드리지 않는다.
 * - **바쁨** — [busy] 는 **버려지지 않은** 티켓 수다. 그래서 취소한 순간 로딩 마스크가 곧바로 걷힌다
 *   (응답을 기다리지 않는다).
 *
 * 키는 「무엇을 읽는가」다(`cust:C-12`·`stores:C-12:1`·`search:라면`). 서로 다른 키는 **함께 나간다** —
 * 한 번호를 나눠 쓰면 사업장 둘을 잇달아 펼쳤을 때 먼저 것이 버려져 그 가지가 영영 「읽는 중」이 된다.
 *
 * ```js
 * data() { return { gate: readGate(), busy: false } }
 * async load(node) {
 *   const ticket = this.gate.start(readKeyOf(node))
 *   if (!ticket) return                       // 같은 요청이 이미 나가 있다(또는 키가 없다)
 *   this.busy = true
 *   try {
 *     const data = await api()
 *     if (!this.gate.isCurrent(ticket)) return // 그 사이 대상이 바뀌었다
 *     this.rows = data
 *   } finally {
 *     this.gate.end(ticket)
 *     this.busy = this.gate.busy()
 *   }
 * }
 * ```
 */
export function readGate() {
  /** 나가 있는 티켓(얼려서 돌려준 그 객체) */
  let tickets = []
  /** 버려진 티켓의 번호 — 티켓 자체를 고쳐 쓰지 않으려고 **번호로** 표시한다 */
  let dropped = []
  let seq = 0

  const live = (ticket) => dropped.indexOf(ticket.id) < 0

  return Object.freeze({
    /**
     * 새 읽기 — 같은 키가 살아 있으면 `null`(보내지 않는다).
     *
     * **키 없는 읽기도 보내지 않는다.** 티켓의 키는 [cancel] 이 앞부분으로 고르는 값이라, `null` 키가
     * 한 장이라도 살아 있으면 다음 `cancel(prefix)` 가 `key.indexOf` 에서 죽는다 — 창 전체가 멈춘다.
     * 키를 만들 수 없는 읽기는 애초에 보낼 대상이 정해지지 않은 읽기다.
     */
    start(key) {
      if (key == null || key === '') return null
      if (tickets.some((ticket) => ticket.key === key && live(ticket))) return null

      seq += 1
      const ticket = Object.freeze({ key, id: seq })
      tickets = tickets.concat(ticket)

      return ticket
    },
    /** 이 응답을 써도 되는가 — 커밋 직전에 묻는다 */
    isCurrent(ticket) {
      return !!ticket && live(ticket)
    },
    /** 끝났다(성공이든 실패든) — 바쁨에서 빠진다 */
    end(ticket) {
      if (!ticket) return
      tickets = tickets.filter((row) => row.id !== ticket.id)
      dropped = dropped.filter((id) => id !== ticket.id)
    },
    /**
     * 대상이 바뀌었다 — 그 키로 나가 있는 응답을 **버린다**. 인자가 없으면 전부다(창을 닫을 때).
     * 키 전체가 아니라 앞부분으로 고른다(`'search:'` 하나로 검색만).
     */
    cancel(prefix) {
      dropped = dropped.concat(
        tickets.filter((ticket) => prefix == null || ticket.key.indexOf(prefix) === 0).map((ticket) => ticket.id)
      )
    },
    /** 아직 기다리는, **버려지지 않은** 읽기가 있는가 */
    busy() {
      return tickets.some(live)
    },
  })
}

/**
 * **한 대상에 가는 요청을 한 줄로 세운다** — [latestGuard]·[readGate] 의 셋째 짝이고 푸는 문제가 다르다.
 *
 * `latestGuard` 는 늦게 온 응답을 **버려서** 순서를 지킨다 — 읽기끼리는 그것으로 된다. 같은 대상에 쓰기와 읽기가 겹치면 아니다:
 * 쓰기 뒤에 나간 읽기가 「최신」이 되어 끝난 쓰기의 응답을 버리고, 서버가 그 읽기를 쓰기보다 먼저 처리했으면 화면에는 쓰기 전
 * 보기가 남는다. 다음 쓰기가 그 보기로 본문을 만들면(본문이 「고른 것 전체」인 쓰기) 앞의 쓰기를 되돌린다. 버려서는 못 푼다 —
 * **겹치지 않게** 한다: 요청마다 앞 요청이 끝난 뒤 돌고, 차례가 온 뒤에 그때의 상태로 요청을 만든다.
 *
 * - [run] — 일 하나를 줄 끝에 세운다. 차례가 오면 `task(current)` 를 부른다. `current()` 는 「이 회차가 아직 살아 있나」이고
 *   응답을 쓰기 직전에 묻는다. **본문·질의는 `task` 안에서 만든다** — 줄에 설 때 만들면 앞 요청의 응답을 못 본다.
 * - [restart] — 대상이 바뀌었다(다른 작업·첫 화면·줄 밖의 요청). 줄에 서 있던 일은 돌지 않고, 나가 있는 일의 `current()` 는
 *   거짓이 된다. 새 회차의 `current` 를 돌려준다(줄 밖 요청이 그것으로 묻는다). 줄은 끊지 않는다 — 새 회차의 첫 일도 나가 있던
 *   요청이 끝난 뒤 돌아서, 같은 대상으로 돌아와도 서버가 받는 순서가 줄의 순서다.
 * - [close] — 화면을 떠난다(되돌아가지 않는다 — `latestGuard.close` 와 같은 뜻).
 *
 * 일이 던져도 줄은 멈추지 않는다 — 그 실패는 `run` 이 돌려준 약속으로만 간다. 반환 객체는 얼려 둔다(`data()` 에 담아도 Vue 의
 * 반응형 프록시가 끼어들지 않는다).
 *
 * ```js
 * data() { return { lane: requestLane() } }
 * choose(change) {
 *   return this.lane.run(async (current) => {
 *     const res = await api(bodyOf(this.view, change)) // 차례가 온 뒤의 보기로 만든다
 *     if (current()) this.view = res.data
 *   })
 * }
 * ```
 */
export function requestLane() {
  /** 줄의 끝 — 마지막으로 선 일이 끝나면(성공이든 실패든) 풀린다 */
  let tail = Promise.resolve()
  let round = 0
  let closed = false

  const currentOf = (mine) => () => !closed && mine === round
  const settled = () => undefined

  return Object.freeze({
    /** 줄 끝에 선다 — 차례가 왔을 때 그 회차가 살아 있으면 `task(current)` 의 답, 아니면 `undefined`(돌지 않았다) */
    run(task) {
      const current = currentOf(round)
      const turn = tail.then(() => (current() ? task(current) : undefined))
      tail = turn.then(settled, settled)
      return turn
    },
    /** 새 회차 — 지금까지 맡긴 일(줄에 선 것·나가 있는 것)을 버리고 새 회차의 `current` 를 돌려준다 */
    restart() {
      round += 1
      return currentOf(round)
    },
    /** 화면을 떠난다 — 그 뒤로는 어떤 회차도 살아 있지 않다(되돌아가지 않는다) */
    close() {
      closed = true
    },
  })
}
